<template>
  <div class="unified-message-renderer">
    <!-- 纯文本消息 -->
    <div v-if="!enableMarkdown" class="message-text" v-html="processedTextContent"></div>
    
    <!-- Markdown 渲染内容 -->
    <div v-else class="message-markdown">
      <!-- 使用自定义的 Markdown 渲染器 -->
      <markdown-renderer
        ref="markdownRenderer"
        :content="displayContent"
        :enable-katex="rendererConfig.katex"
        :enable-mermaid="rendererConfig.mermaid"
        :enable-highlight="rendererConfig.highlight"
        :streaming="streaming"
        @rendered="onRendered"
      />
    </div>
    
    <!-- 代码复制按钮等增强功能 -->
    <div v-if="showEnhancements" class="message-enhancements">
      <slot name="enhancements"></slot>
    </div>
  </div>
</template>

<script>
import MarkdownRenderer from './renderers/MarkdownRenderer.vue';
import MessageTypeAdapter from './MessageTypeAdapter';

export default {
  name: 'UnifiedMessageRenderer',
  
  components: {
    MarkdownRenderer
  },
  
  props: {
    // 消息对象
    message: {
      type: Object,
      required: true
    },
    
    // 是否正在流式传输
    streaming: {
      type: Boolean,
      default: false
    },
    
    // 强制使用的格式
    forceFormat: {
      type: String,
      default: null
    },
    
    // 是否显示增强功能
    showEnhancements: {
      type: Boolean,
      default: true
    },
    
    // 自定义配置
    customConfig: {
      type: Object,
      default: () => ({})
    }
  },
  
  data() {
    return {
      // 内部内容缓存（用于流式更新）
      internalContent: '',
      // 渲染完成标记
      renderComplete: false
    };
  },
  
  computed: {
    // 适配后的消息
    adaptedMessage() {
      return MessageTypeAdapter.adapt(this.message);
    },
    
    // 消息类型
    messageType() {
      if (this.forceFormat) {
        return this.forceFormat;
      }
      
      // 智能体消息默认使用 markdown
      if (this.adaptedMessage.type === 'AGENT' || this.adaptedMessage.isAgent) {
        return 'markdown';
      }
      
      // 检查消息格式字段
      if (this.adaptedMessage.format === 'markdown') {
        return 'markdown';
      }
      
      // 流式消息
      if (this.adaptedMessage.streaming || this.streaming) {
        return 'streaming';
      }
      
      // 检测内容是否包含 Markdown 语法
      if (this.hasMarkdownSyntax(this.adaptedMessage.content)) {
        return 'markdown';
      }
      
      return 'text';
    },
    
    // 是否启用 Markdown 渲染
    enableMarkdown() {
      return this.messageType !== 'text';
    },
    
    // 显示的内容
    displayContent() {
      return this.adaptedMessage.content || this.internalContent || '';
    },
    
    // 处理后的文本内容（用于纯文本显示）
    processedTextContent() {
      let content = this.displayContent;
      
      // 转换换行符为 <br>
      content = content.replace(/\n/g, '<br>');
      
      // 处理链接
      content = content.replace(
        /(https?:\/\/[^\s<]+)/g,
        '<a href="$1" target="_blank" rel="noopener">$1</a>'
      );
      
      return content;
    },
    
    // 渲染器配置
    rendererConfig() {
      const defaultConfig = {
        markdown: this.enableMarkdown,
        katex: false,
        mermaid: false,
        highlight: true
      };
      
      // 根据消息类型自动配置
      if (this.adaptedMessage.features) {
        Object.assign(defaultConfig, this.adaptedMessage.features);
      }
      
      // 智能检测内容特征
      const content = this.displayContent;
      if (content) {
        defaultConfig.katex = this.hasLatex(content);
        defaultConfig.mermaid = this.hasMermaid(content);
        defaultConfig.highlight = this.hasCodeBlock(content);
      }
      
      // 应用自定义配置
      return Object.assign(defaultConfig, this.customConfig);
    }
  },
  
  watch: {
    'message.content': {
      handler(newContent) {
        if (!this.streaming) {
          this.internalContent = newContent || '';
        }
      },
      immediate: true
    }
  },
  
  methods: {
    // 检测是否包含 Markdown 语法
    hasMarkdownSyntax(content) {
      if (!content) return false;
      
      // 检测常见的 Markdown 语法
      const patterns = [
        /^#{1,6}\s/m,           // 标题
        /\*\*[^*]+\*\*/,        // 粗体
        /\*[^*]+\*/,            // 斜体
        /```[\s\S]*?```/,       // 代码块
        /`[^`]+`/,              // 行内代码
        /^\s*[-*+]\s/m,         // 无序列表
        /^\s*\d+\.\s/m,         // 有序列表
        /\[([^\]]+)\]\(([^)]+)\)/, // 链接
        /^\|.*\|$/m,            // 表格
        /^>/m                   // 引用
      ];
      
      return patterns.some(pattern => pattern.test(content));
    },
    
    // 检测是否包含 LaTeX 公式
    hasLatex(content) {
      if (!content) return false;
      // 检测 $ ... $ 或 $$ ... $$ 或 \[ ... \] 或 \( ... \)
      return /\$[^$]+\$|\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)/.test(content);
    },
    
    // 检测是否包含 Mermaid 图表
    hasMermaid(content) {
      if (!content) return false;
      return /```mermaid[\s\S]*?```/.test(content);
    },
    
    // 检测是否包含代码块
    hasCodeBlock(content) {
      if (!content) return false;
      return /```[\s\S]*?```/.test(content);
    },
    
    // 追加流式内容
    appendContent(chunk) {
      if (this.streaming) {
        this.internalContent += chunk;
        
        // 通知 Markdown 渲染器更新
        if (this.$refs.markdownRenderer) {
          this.$refs.markdownRenderer.updateContent(this.internalContent);
        }
      }
    },
    
    // 设置完整内容
    setContent(content) {
      this.internalContent = content;
    },
    
    // 完成流式传输
    finishStreaming() {
      if (this.$refs.markdownRenderer) {
        this.$refs.markdownRenderer.finishStreaming();
      }
    },
    
    // 渲染完成回调
    onRendered() {
      this.renderComplete = true;
      this.$emit('rendered');
    },
    
    // 获取渲染后的HTML（用于导出等）
    getRenderedHTML() {
      if (this.$refs.markdownRenderer) {
        return this.$refs.markdownRenderer.getRenderedHTML();
      }
      return this.processedTextContent;
    }
  },
  
  mounted() {
    // 初始化内容
    if (this.message.content) {
      this.internalContent = this.message.content;
    }
  }
};
</script>

<style lang="scss" scoped>
.unified-message-renderer {
  // 使用CSS变量保持主题一致性
  --msg-text-color: var(--im-text-primary, #333);
  --msg-bg-color: var(--im-bg-primary, #fff);
  --msg-link-color: var(--im-link-color, #0066cc);
  --msg-code-bg: var(--im-code-bg, #f6f8fa);
  --msg-code-color: var(--im-code-color, #032f62);
  --msg-border-color: var(--im-border-color, #e1e4e8);
  
  color: var(--msg-text-color);
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;
  
  // 纯文本样式
  .message-text {
    white-space: pre-wrap;
    
    ::v-deep a {
      color: var(--msg-link-color);
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
  
  // Markdown 容器样式
  .message-markdown {
    // 样式将在 MarkdownRenderer 组件中定义
  }
  
  // 增强功能区域
  .message-enhancements {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--msg-border-color);
  }
}

// 暗色主题支持
@media (prefers-color-scheme: dark) {
  .unified-message-renderer {
    --msg-text-color: #e1e4e8;
    --msg-bg-color: #0d1117;
    --msg-link-color: #58a6ff;
    --msg-code-bg: #161b22;
    --msg-code-color: #79c0ff;
    --msg-border-color: #30363d;
  }
}
</style>